import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConversionSuccessProps {
  fileName: string | null;
  originalName?: string | null;
  accentColor?: string;
  extraInfo?: React.ReactNode;
  onDownload: () => void;
  onReset: () => void;
}

const SHARE_URL = typeof window !== 'undefined' ? window.location.href : '';

const shareChannels = [
  {
    key: 'wechat',
    icon: 'ri-wechat-line',
    label: '微信',
    color: 'bg-green-500 hover:bg-green-600',
    action: (url: string, text: string) => {
      // 微信分享：复制链接提示用户
      navigator.clipboard?.writeText(`${text} ${url}`);
      return 'wechat';
    },
  },
  {
    key: 'weibo',
    icon: 'ri-weibo-line',
    label: '微博',
    color: 'bg-red-500 hover:bg-red-600',
    action: (url: string, text: string) => {
      window.open(
        `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`,
        '_blank'
      );
      return 'weibo';
    },
  },
  {
    key: 'twitter',
    icon: 'ri-twitter-x-line',
    label: 'X',
    color: 'bg-gray-900 hover:bg-black',
    action: (url: string, text: string) => {
      window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
        '_blank'
      );
      return 'twitter';
    },
  },
  {
    key: 'facebook',
    icon: 'ri-facebook-circle-line',
    label: 'Facebook',
    color: 'bg-sky-600 hover:bg-sky-700',
    action: (url: string) => {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        '_blank'
      );
      return 'facebook';
    },
  },
  {
    key: 'copy',
    icon: 'ri-links-line',
    label: '复制链接',
    color: 'bg-gray-500 hover:bg-gray-600',
    action: (url: string) => {
      navigator.clipboard?.writeText(url);
      return 'copy';
    },
  },
];

export default function ConversionSuccess({
  fileName,
  originalName,
  accentColor = 'green',
  extraInfo,
  onDownload,
  onReset,
}: ConversionSuccessProps) {
  const { t } = useTranslation();
  const [showShare, setShowShare] = useState(false);
  const [sharedChannel, setSharedChannel] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const shareText = t('share.text', { defaultValue: '推荐一个超好用的免费PDF工具，无需注册，一键转换！' });
  const shareUrl = SHARE_URL;

  const handleShare = (channel: typeof shareChannels[0]) => {
    const result = channel.action(shareUrl, shareText);
    setSharedChannel(result);
    if (result === 'copy' || result === 'wechat') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const accentMap: Record<string, { ring: string; bg: string; text: string; btn: string; btnHover: string }> = {
    green:  { ring: 'border-green-200',  bg: 'bg-green-50',  text: 'text-green-600',  btn: 'bg-green-600',  btnHover: 'hover:bg-green-700' },
    teal:   { ring: 'border-teal-200',   bg: 'bg-teal-50',   text: 'text-teal-600',   btn: 'bg-teal-600',   btnHover: 'hover:bg-teal-700' },
    orange: { ring: 'border-orange-200', bg: 'bg-orange-50', text: 'text-orange-600', btn: 'bg-orange-600', btnHover: 'hover:bg-orange-700' },
    pink:   { ring: 'border-pink-200',   bg: 'bg-pink-50',   text: 'text-pink-600',   btn: 'bg-pink-600',   btnHover: 'hover:bg-pink-700' },
    indigo: { ring: 'border-indigo-200', bg: 'bg-indigo-50', text: 'text-indigo-600', btn: 'bg-indigo-600', btnHover: 'hover:bg-indigo-700' },
  };
  const c = accentMap[accentColor] ?? accentMap.green;

  return (
    <div className={`border-2 ${c.ring} rounded-2xl overflow-hidden`}>
      {/* 成功主区域 */}
      <div className={`${c.bg} px-8 pt-10 pb-8 text-center`}>
        {/* 动画勾选图标 */}
        <div className="relative w-20 h-20 mx-auto mb-5">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-md">
            <i className={`ri-checkbox-circle-fill text-5xl ${c.text}`}></i>
          </div>
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
            <i className="ri-star-fill text-white text-xs"></i>
          </span>
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-1">{t('upload.done')}</h3>
        <p className="text-gray-500 text-sm mb-1">{t('share.successHint', { defaultValue: '文件已转换完成，可以下载了！' })}</p>

        {originalName && (
          <p className="text-xs text-gray-400 mb-1 truncate max-w-xs mx-auto">{originalName}</p>
        )}
        {fileName && (
          <p className={`text-sm font-medium ${c.text} mb-4 truncate max-w-xs mx-auto`}>{fileName}</p>
        )}

        {extraInfo && <div className="mb-5">{extraInfo}</div>}

        {/* 主操作按钮 */}
        <div className="flex flex-wrap gap-3 justify-center mb-6">
          <button
            onClick={onDownload}
            className={`flex items-center gap-2 px-7 py-3 ${c.btn} ${c.btnHover} text-white rounded-xl font-semibold shadow-sm transition-all whitespace-nowrap cursor-pointer`}
          >
            <i className="ri-download-2-line text-lg"></i>
            {t('upload.download')}
          </button>
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-7 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl font-semibold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line text-lg"></i>
            {t('upload.convertAnother')}
          </button>
        </div>

        {/* 分享区域触发按钮 */}
        <button
          onClick={() => setShowShare(!showShare)}
          className="inline-flex items-center gap-2 px-5 py-2 bg-white/80 border border-gray-200 rounded-full text-sm font-medium text-gray-600 hover:bg-white hover:shadow-sm transition-all cursor-pointer"
        >
          <i className="ri-share-forward-line text-base"></i>
          {t('share.shareToFriend', { defaultValue: '分享给朋友' })}
          <i className={`ri-arrow-${showShare ? 'up' : 'down'}-s-line text-gray-400`}></i>
        </button>
      </div>

      {/* 分享展开面板 */}
      {showShare && (
        <div className="bg-white border-t border-gray-100 px-8 py-6">
          <p className="text-center text-sm text-gray-500 mb-4">
            <i className="ri-heart-line text-red-400 mr-1"></i>
            {t('share.helpText', { defaultValue: '觉得好用？分享给需要的朋友吧 😊' })}
          </p>

          <div className="flex flex-wrap gap-3 justify-center mb-4">
            {shareChannels.map((ch) => (
              <button
                key={ch.key}
                onClick={() => handleShare(ch)}
                className={`flex items-center gap-2 px-4 py-2 ${ch.color} text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer shadow-sm`}
              >
                <i className={`${ch.icon} text-base`}></i>
                {ch.key === 'copy' && copied ? (
                  <span className="flex items-center gap-1">
                    <i className="ri-check-line"></i>
                    {t('share.copied', { defaultValue: '已复制！' })}
                  </span>
                ) : (
                  ch.label
                )}
              </button>
            ))}
          </div>

          {/* 微信提示 */}
          {sharedChannel === 'wechat' && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-center text-sm text-green-700">
              <i className="ri-information-line mr-1"></i>
              {t('share.wechatTip', { defaultValue: '链接已复制，请打开微信粘贴分享给朋友！' })}
            </div>
          )}

          {/* 复制成功提示 */}
          {copied && sharedChannel === 'copy' && (
            <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-center text-sm text-gray-600">
              <i className="ri-check-double-line mr-1 text-green-500"></i>
              {t('share.copySuccess', { defaultValue: '链接已复制到剪贴板！' })}
            </div>
          )}

          {/* 分享文案预览 */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-500 text-center leading-relaxed">
            <i className="ri-double-quotes-l text-gray-300"></i>
            {shareText}
            <i className="ri-double-quotes-r text-gray-300"></i>
          </div>
        </div>
      )}
    </div>
  );
}
